import {cn} from '@common-web/lib/utils';
import type {ConfigChange} from '@common/enrollmentSite/configDiff';

// GitHub-PR-style change list: grouped by builder section, removed values struck in
// red, new values bold in green. Used by the publish dialog and version history.
// `scroll` false lets an outer container own the scroll (avoids a nested scrollbar).
export function ConfigDiffList({
  changes,
  className,
  scroll = true,
}: {
  changes: ConfigChange[];
  className?: string;
  scroll?: boolean;
}) {
  const sections: {name: string; items: ConfigChange[]}[] = [];
  for (const c of changes) {
    let s = sections.find((x) => x.name === c.section);
    if (!s) {
      s = {name: c.section, items: []};
      sections.push(s);
    }
    s.items.push(c);
  }
  return (
    <div className={cn('rounded-lg border', scroll && 'max-h-64 overflow-y-auto', className)}>
      {sections.map((s) => (
        <div key={s.name} className='border-b last:border-b-0'>
          <div className='bg-muted/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
            {s.name}
          </div>
          <ul className='divide-y'>
            {s.items.map((c, i) => (
              <li key={i} className='flex items-start gap-2 px-3 py-1.5 text-sm'>
                <span
                  className={cn(
                    'mt-0.5 w-3 shrink-0 text-center font-bold',
                    c.kind === 'added' && 'text-emerald-600',
                    c.kind === 'removed' && 'text-red-600',
                    c.kind === 'changed' && 'text-amber-600',
                  )}
                >
                  {c.kind === 'added' ? '+' : c.kind === 'removed' ? '−' : '~'}
                </span>
                <div className='min-w-0 flex-1 break-words'>
                  <span className='text-neutral-700'>{c.label}</span>
                  {c.before || c.after ? (
                    <span className='ml-1.5'>
                      {c.before && (
                        <span className='rounded bg-red-50 px-1 text-red-700 line-through [overflow-wrap:anywhere]'>
                          {c.before}
                        </span>
                      )}
                      {c.before && c.after && <span className='mx-1 text-muted-foreground'>→</span>}
                      {c.after && (
                        <span className='rounded bg-emerald-50 px-1 font-semibold text-emerald-700 [overflow-wrap:anywhere]'>
                          {c.after}
                        </span>
                      )}
                    </span>
                  ) : (
                    c.kind === 'changed' && (
                      <span className='ml-1.5 text-xs italic text-muted-foreground'>
                        {c.note ?? 'edited'}
                      </span>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
